// Generated by Claude Code
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { CustomFieldsEditor, type CustomFieldsAdapter } from '#components/domain/operations/settings/shared/custom-fields-editor.tsx'
import { FormSettingsEditor } from '#components/domain/operations/settings/form-settings-editor.tsx'
import { PartTagsSettings } from '#components/domain/operations/parts/settings/part-tags-settings.tsx'
import { PartQrLayoutSettings } from '#components/domain/operations/parts/settings/part-qr-layout-settings.tsx'
import { Tabs } from '#components/primitives/components/tabs.tsx'
import PartsApi from '#core/api/operations/Parts.ts'
import { PartElevated, PermissionAction, PermissionService } from '#core/entities/auth/roles/Permission.ts'
import { useCanEditSetting } from '#core/entities/auth/roles/entity-settings-gates.ts'
import { useUserState } from '#core/state/UserState.ts'
import { useQuickSettingsReturn } from '#hooks/use-quick-settings-return.ts'

export const Route = createFileRoute('/_appSettings/$companyId/settings/parts')({
  component: RouteComponent,
})

const partsApi = new PartsApi()

const customFieldsAdapter: CustomFieldsAdapter = {
  load: () => partsApi.getPartCustomPropertiesDefinition(),
  save: (definition) => partsApi.upsertPartCustomPropertiesDefinition(definition),
}

/**
 * Part field keys in the order they appear in the New Part form. Keys match
 * `PartCreateRequestSchema` property names, except the virtual UI fields
 * (`profile_picture`, `tags`, `files`, `subscribers`, `locations`, `location_notes`,
 * `stock`, `vendors`, `substitutes`), whose `required` flag is enforced client-side only.
 */
const PART_FIELD_ORDER = [
  'name',
  'profile_picture',
  'files',
  'description',
  'custom_id',
  'serial_number',
  'condition',
  'criticality',
  'qr_code_data',
  'locations',
  'location_notes',
  'stock',
  'minimum_stock',
  'asset_ids',
  'substitutes',
  'tags',
  'vendors',
  'subscribers',
] as const

const PART_ALWAYS_VISIBLE = ['name']
const PART_ALWAYS_REQUIRED = ['name']

type PartsSettingsTab = 'form' | 'tags' | 'custom-fields' | 'qr-code'

/** Settings page for Parts: part form, tag catalogue, custom field definitions and QR label. */
function RouteComponent() {
  const { t } = useTranslation('settings')
  const { t: tGeneral } = useTranslation('general')
  const { t: tParts } = useTranslation('parts')
  const [activeTab, setActiveTab] = useState<PartsSettingsTab>('form')
  const userState = useUserState()
  const { showClose, handleClose } = useQuickSettingsReturn()

  // Per-tab permission gating: a user only sees the tabs whose write bit they hold.
  const canEditForm = userState.hasPermission(PermissionService.PARTS, PermissionAction.FORM_SETTINGS)
  const canManageTags = userState.hasPermission(PermissionService.PARTS, PartElevated.MANAGE_TAGS)
  const canManage = userState.hasPermission(PermissionService.PARTS, PermissionAction.CUSTOM_PROPERTIES)
  const canManageQr = useCanEditSetting('part', 'qr_code_template')

  const tabs = [
    canEditForm && { key: 'form' as const, label: t('partsSettings.tabForm') },
    canManageTags && { key: 'tags' as const, label: t('shared.fields.tags') },
    canManage && { key: 'custom-fields' as const, label: t('shared.fields.customFields') },
    canManageQr && { key: 'qr-code' as const, label: t('partsSettings.tabQrCode') },
  ].filter(Boolean) as { key: PartsSettingsTab; label: string }[]

  // Fall back to the first permitted tab when the active one is hidden (e.g. a deep link).
  const effectiveTab = tabs.some((tb) => tb.key === activeTab) ? activeTab : tabs[0]?.key

  const fieldLabels: Record<string, string> = {
    name: t('shared.fields.name'),
    profile_picture: t('shared.fields.profilePicture'),
    description: t('shared.fields.description'),
    serial_number: t('shared.fields.serialNumber'),
    custom_id: t('shared.fields.customId'),
    qr_code_data: t('shared.fields.customQrCode'),
    tags: t('shared.fields.tags'),
    condition: tParts('columns.condition'),
    criticality: t('shared.fields.criticality'),
    stock: tParts('editDrawer.fields.stock'),
    minimum_stock: tParts('editDrawer.fields.minimumStock'),
    files: t('shared.fields.attachments'),
    subscribers: t('shared.fields.subscribers'),
    locations: tParts('detail.locations'),
    location_notes: tParts('editDrawer.fields.locationNotes'),
    asset_ids: t('shared.fields.relatedAssets'),
    vendors: tParts('detail.vendors'),
    substitutes: tParts('columns.substitutes'),
  }

  return (
    <div className="p-4 overflow-y-auto h-100">
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h1 className="h4 fw-bold mb-1">{t('partsSettings.title')}</h1>
          <p className="text-secondary mb-0">{t('partsSettings.description')}</p>
        </div>
        {showClose && (
          <button
            type="button"
            className="btn btn-sm btn-icon btn-outline-secondary border-0"
            onClick={handleClose}
            aria-label={t('shared.actions.close')}
          >
            <X size={20} />
          </button>
        )}
      </div>

      {tabs.length === 0 ? (
        <div className="text-secondary">
          <h2 className="h6 fw-bold mb-1">{tGeneral('notAuthorized.title')}</h2>
          <p className="mb-0">{tGeneral('notAuthorized.message')}</p>
        </div>
      ) : (
        <>
          <Tabs
            tabs={tabs}
            activeKey={effectiveTab}
            onChange={(key) => setActiveTab(key as PartsSettingsTab)}
            variant="underline"
            size="sm"
            className="mb-4"
          />

          {effectiveTab === 'form' && (
            <div>
              <p className="text-secondary mb-4">{t('partsSettings.formDescription')}</p>
              <FormSettingsEditor
                entityType="part"
                fieldLabels={fieldLabels}
                orderedFields={[...PART_FIELD_ORDER]}
                alwaysVisibleFields={PART_ALWAYS_VISIBLE}
                alwaysRequiredFields={PART_ALWAYS_REQUIRED}
                canEdit={canEditForm}
              />
            </div>
          )}

          {effectiveTab === 'tags' && (
            <div>
              <p className="text-secondary small mb-4">{t('partsSettings.tagsDescription')}</p>
              <PartTagsSettings />
            </div>
          )}

          {effectiveTab === 'custom-fields' && (
            <div>
              <p className="text-secondary mb-3">{t('partsSettings.customFieldsDescription')}</p>
              <CustomFieldsEditor adapter={customFieldsAdapter} canManage={canManage} />
            </div>
          )}

          {effectiveTab === 'qr-code' && <PartQrLayoutSettings />}
        </>
      )}
    </div>
  )
}
